import * as React from 'react';
import { Check, Globe } from 'lucide-react';
import { WorkspaceButton, useToast } from '@/components/ui/workspace';
import {
  errorMessage,
  rowsOf,
  useConfirmItems,
  useItemAction,
  useKbFlagged,
  useKbItem,
  type KbFlaggedRow,
} from '@/lib/api/vendor-knowledge';
import { cn } from '@/lib/utils/cn';
import {
  Actions,
  Chip,
  FieldHelp,
  FieldLabel,
  FormError,
  InlineForm,
  KindChip,
  MonoNo,
  Quote,
} from '../kb-bits';
import { formatDate, hostPath, plural, skipReasonText } from '../kb-logic';
import { ReviewGroupShell } from './proposals';
import { useVendorPage } from '../vendor-page-context';
import { TextArea } from './review-bits';

/**
 * Flagged items (E13-F07): the page changed and no longer has the item's
 * words; Caliber v1's summary is not on the vendor's page; the page is
 * gone; a protected name was found in a confirmed item. Nothing is edited
 * in place: an item is replaced (a new numbered item supersedes it),
 * retired with a reason, or kept with a note (rule 19).
 */
export function FlagGroups({ vendorId }: { vendorId: string }) {
  const flagged = useKbFlagged(vendorId, true);
  const rows = rowsOf(flagged.data);
  const of = (reason: string) => rows.filter((r) => r.flag?.reason === reason);
  const changed = of('source_changed');
  const notFound = of('not_found_on_source');
  const gone = of('source_unavailable');
  const names = of('client_name');
  const goneBySource = groupBy(gone, (r) => r.source.url ?? r.source.title ?? r.id);
  return (
    <>
      {changed.length > 0 ? (
        <ReviewGroupShell
          id="kb-review-changed"
          title="Source changed"
          count={changed.length}
          hint="The page no longer has the item’s words. Replacing keeps both items; nothing is edited in place."
        >
          {changed.map((r) => (
            <ChangedCard key={r.id} row={r} />
          ))}
        </ReviewGroupShell>
      ) : null}
      {notFound.length > 0 ? (
        <ReviewGroupShell
          id="kb-review-notfound"
          title="Not found on the vendor’s page"
          count={notFound.length}
          hint="Each summary came from the Avero Advisors Vendor Knowledge Base, and the vendor’s page no longer says it."
        >
          {notFound.map((r) => (
            <NotFoundCard key={r.id} row={r} />
          ))}
        </ReviewGroupShell>
      ) : null}
      {gone.length > 0 ? (
        <ReviewGroupShell
          id="kb-review-gone"
          title="Page no longer available"
          count={gone.length}
          hint="The items keep their dates until you decide."
        >
          {goneBySource.map(([key, items]) => (
            <GoneCard key={key} rows={items} />
          ))}
        </ReviewGroupShell>
      ) : null}
      {names.length > 0 ? (
        <ReviewGroupShell
          id="kb-review-client"
          title="Client name found"
          count={names.length}
          hint="A name protected after these items were confirmed. Replacing one makes a new item with the name removed; the old one stays on record."
        >
          {names.map((r) => (
            <ClientNameCard key={r.id} row={r} />
          ))}
        </ReviewGroupShell>
      ) : null}
      {flagged.hasNextPage ? (
        <WorkspaceButton
          variant="tertiary"
          onClick={() => void flagged.fetchNextPage()}
          disabled={flagged.isFetchingNextPage}
        >
          Show more flagged items
        </WorkspaceButton>
      ) : null}
    </>
  );
}

function groupBy<T>(rows: T[], key: (r: T) => string): Array<[string, T[]]> {
  const map = new Map<string, T[]>();
  for (const r of rows) {
    const k = key(r);
    map.set(k, [...(map.get(k) ?? []), r]);
  }
  return Array.from(map.entries());
}

function FlagShell({
  tone,
  children,
}: {
  tone: 'changed' | 'gone' | 'client' | 'notfound';
  children: React.ReactNode;
}) {
  return (
    <div
      className={cn(
        'rounded-card border border-l-4 border-ink-100 bg-paper-0 px-4 py-3.5',
        tone === 'changed' && 'border-l-posture-at-risk',
        tone === 'gone' && 'border-l-posture-critical',
        tone === 'client' && 'border-l-accent-amber-600',
        tone === 'notfound' && 'border-l-posture-unassessed',
      )}
    >
      {children}
    </div>
  );
}

function FlagHead({ row }: { row: KbFlaggedRow }) {
  return (
    <div className="flex items-center gap-2.5">
      {row.number ? <MonoNo>{row.number}</MonoNo> : null}
      <span className="min-w-0 flex-1 font-semibold text-ink-900">{row.statement}</span>
      <KindChip kind={row.kind} />
    </div>
  );
}

/** "Why …?" with one line of text and the button that acts (retire, keep). */
export function ReasonForm({
  label,
  placeholder,
  help,
  button,
  destructive = false,
  pending,
  error,
  onSubmit,
  onCancel,
}: {
  label: string;
  placeholder?: string;
  help?: React.ReactNode;
  button: string;
  destructive?: boolean;
  pending?: boolean;
  error?: string | null;
  onSubmit: (text: string) => void;
  onCancel: () => void;
}) {
  const [text, setText] = React.useState('');
  const id = React.useId();
  return (
    <InlineForm>
      <FieldLabel htmlFor={id}>{label}</FieldLabel>
      <div className="flex flex-wrap items-center gap-2">
        <input
          id={id}
          autoFocus
          className="h-input-h min-w-0 flex-1 rounded-card border border-ink-200 bg-paper-0 px-3 text-body text-ink-900 placeholder:text-ink-300 focus:outline focus:outline-2 focus:outline-offset-2 focus:outline-accent-amber-600"
          value={text}
          placeholder={placeholder}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && text.trim()) onSubmit(text.trim());
            if (e.key === 'Escape') onCancel();
          }}
          autoComplete="off"
        />
        <WorkspaceButton
          variant={destructive ? 'destructive' : 'primary'}
          disabled={!text.trim() || pending}
          onClick={() => onSubmit(text.trim())}
        >
          {button}
        </WorkspaceButton>
        <WorkspaceButton variant="tertiary" onClick={onCancel}>
          Cancel
        </WorkspaceButton>
      </div>
      {error ? <FormError>{error}</FormError> : null}
      {help ? <FieldHelp>{help}</FieldHelp> : null}
    </InlineForm>
  );
}

/** Retire one item, or keep it with a note (clears its flag). */
function RetireOrKeep({
  ids,
  keepLabel,
  mode,
  setMode,
  onDone,
}: {
  ids: string[];
  keepLabel: string;
  mode: 'retire' | 'keep' | null;
  setMode: (m: 'retire' | 'keep' | null) => void;
  onDone?: () => void;
}) {
  const { toast } = useToast();
  const action = useItemAction();
  const [error, setError] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);
  const run = async (verb: 'retire' | 'clear-flag', text: string) => {
    setBusy(true);
    setError(null);
    try {
      for (const id of ids) {
        await action.mutateAsync({
          id,
          verb,
          body: verb === 'retire' ? { reason: text } : { note: text },
        });
      }
      toast({
        message:
          verb === 'retire'
            ? `Retired ${plural(ids.length, 'item')}. ${ids.length === 1 ? 'It stays' : 'They stay'} on record and ${ids.length === 1 ? 'is' : 'are'} no longer offered for maps.`
            : `Kept ${plural(ids.length, 'item')}. ${ids.length === 1 ? 'It keeps its' : 'Each keeps its'} date.`,
      });
      setMode(null);
      onDone?.();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  if (mode === 'retire') {
    return (
      <ReasonForm
        label={ids.length === 1 ? 'Why retire it?' : `Why retire these ${ids.length} items?`}
        placeholder="For example: no longer offered"
        help="A retired item stays on record and is no longer offered for maps."
        button={ids.length === 1 ? 'Retire' : `Retire ${plural(ids.length, 'item')}`}
        destructive
        pending={busy}
        error={error}
        onSubmit={(t) => void run('retire', t)}
        onCancel={() => setMode(null)}
      />
    );
  }
  if (mode === 'keep') {
    return (
      <ReasonForm
        label={`${keepLabel}: why is ${ids.length === 1 ? 'it' : 'each'} still right?`}
        placeholder="For example: the same words are on the new release notes"
        help="Kept items keep their date; the note is recorded with them."
        button="Keep"
        pending={busy}
        error={error}
        onSubmit={(t) => void run('clear-flag', t)}
        onCancel={() => setMode(null)}
      />
    );
  }
  return null;
}

function codePointLength(s: string): number {
  return Array.from(s).length;
}

function ChangedCard({ row }: { row: KbFlaggedRow }) {
  const { toast } = useToast();
  const detail = useKbItem(row.id);
  const supersede = useItemAction();
  const confirm = useConfirmItems();
  const [mode, setMode] = React.useState<'retire' | 'keep' | 'replace' | null>(null);
  const [statement, setStatement] = React.useState(row.statement ?? '');
  const [error, setError] = React.useState<string | null>(null);
  const flag = detail.data?.flag;
  const now = flag?.passages?.now[0] ?? null;
  const newVersion =
    typeof flag?.detail?.['new_version_id'] === 'string'
      ? (flag.detail['new_version_id'] as string)
      : null;
  const flaggedOn = formatDate(row.flag?.flagged_at);
  const asOf = formatDate(row.as_of);

  const replace = async () => {
    if (!now || !newVersion) return;
    setError(null);
    try {
      const made = (await supersede.mutateAsync({
        id: row.id,
        verb: 'supersede',
        body: {
          source_version_id: newVersion,
          segment_id: now.segment_id,
          start: 0,
          end: codePointLength(now.text),
          statement: statement.trim(),
        },
      })) as { id?: string };
      if (!made.id) return;
      const reply = await confirm.mutateAsync({ ids: [made.id], acknowledge: [made.id] });
      const done = reply.confirmed[0];
      if (done) {
        toast({ message: `${done.number} replaces ${row.number}. Both are kept.` });
      } else {
        const skip = reply.skipped[0];
        toast({
          message: `The replacement waits under New: ${skip ? skipReasonText(skip.reason) : 'it needs a look'}.`,
        });
      }
      setMode(null);
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  return (
    <FlagShell tone="changed">
      <FlagHead row={row} />
      <div className="mt-1.5 text-meta text-ink-500">
        Confirmed from this page’s capture of {row.citation.parts.date}. A check on {flaggedOn}{' '}
        found the words gone.
        {row.source.url ? (
          <span className="ml-1 inline-flex items-center gap-1 font-mono text-[11px]">
            <Globe className="h-3 w-3" aria-hidden="true" /> {hostPath(row.source.url)}
          </span>
        ) : null}
      </div>
      <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
        <div className="rounded-card border border-ink-100 bg-paper-50 px-3 pb-3 pt-2.5">
          <div className="text-[11px] font-medium uppercase leading-[18px] tracking-[0.06em] text-ink-500">
            The item quotes
          </div>
          <Quote text={row.excerpt_text} variant="old" />
        </div>
        <div className="rounded-card border border-ink-100 bg-paper-50 px-3 pb-3 pt-2.5">
          <div className="text-[11px] font-medium uppercase leading-[18px] tracking-[0.06em] text-ink-500">
            Nearest passage on the page now
          </div>
          {detail.isPending ? (
            <div className="mt-2 text-meta text-ink-500">Loading the page as captured…</div>
          ) : now ? (
            <>
              <Quote text={now.text} />
              <div className="mt-1.5 flex items-center gap-[5px] text-meta text-posture-healthy">
                <Check className="h-3 w-3" aria-hidden="true" /> Quote found on the captured page,{' '}
                {flaggedOn}
              </div>
            </>
          ) : (
            <div className="mt-2 text-meta text-ink-500">
              Nothing under the same heading any more.
            </div>
          )}
        </div>
      </div>
      {mode === 'replace' ? (
        <InlineForm aria-label="Replace with the new wording">
          <FieldLabel htmlFor={`stmt-${row.id}`}>Statement for the new item</FieldLabel>
          <TextArea
            id={`stmt-${row.id}`}
            value={statement}
            onChange={(e) => setStatement(e.target.value)}
          />
          <FieldHelp>
            A new numbered item quotes the page as it is now and replaces {row.number}, which stays
            on record. A number in the statement must appear in the quote.
          </FieldHelp>
          {error ? <FormError>{error}</FormError> : null}
          <Actions>
            <WorkspaceButton
              variant="primary"
              disabled={!statement.trim() || supersede.isPending || confirm.isPending}
              onClick={() => void replace()}
            >
              Replace
            </WorkspaceButton>
            <WorkspaceButton variant="tertiary" onClick={() => setMode(null)}>
              Cancel
            </WorkspaceButton>
          </Actions>
        </InlineForm>
      ) : mode ? (
        <RetireOrKeep
          ids={[row.id]}
          keepLabel={`Keep as of ${asOf}`}
          mode={mode}
          setMode={(m) => setMode(m)}
        />
      ) : (
        <Actions>
          {now && newVersion ? (
            <WorkspaceButton variant="primary" onClick={() => setMode('replace')}>
              Replace with the new wording
            </WorkspaceButton>
          ) : null}
          <WorkspaceButton onClick={() => setMode('retire')}>Retire</WorkspaceButton>
          <WorkspaceButton variant="tertiary" onClick={() => setMode('keep')}>
            Keep as of {asOf}
          </WorkspaceButton>
        </Actions>
      )}
    </FlagShell>
  );
}

function NotFoundCard({ row }: { row: KbFlaggedRow }) {
  const [mode, setMode] = React.useState<'retire' | 'keep' | null>(null);
  const asOf = formatDate(row.as_of);
  return (
    <FlagShell tone="notfound">
      <FlagHead row={row} />
      <Quote text={row.excerpt_text} className="mt-2.5" />
      <div className="mt-1.5 text-meta text-ink-500">
        From the Avero Advisors Vendor Knowledge Base, as of {asOf}. The vendor’s page, captured{' '}
        {formatDate(row.flag?.flagged_at)}, no longer says it. Anything the page says now is
        proposed under New.
      </div>
      {mode ? (
        <RetireOrKeep
          ids={[row.id]}
          keepLabel={`Keep as of ${asOf}`}
          mode={mode}
          setMode={setMode}
        />
      ) : (
        <Actions>
          <WorkspaceButton onClick={() => setMode('retire')}>Retire</WorkspaceButton>
          <WorkspaceButton variant="tertiary" onClick={() => setMode('keep')}>
            Keep as of {asOf}
          </WorkspaceButton>
        </Actions>
      )}
    </FlagShell>
  );
}

function GoneCard({ rows }: { rows: KbFlaggedRow[] }) {
  const { openForm } = useVendorPage();
  const [mode, setMode] = React.useState<'retire' | 'keep' | null>(null);
  const first = rows[0]!;
  const dates = Array.from(new Set(rows.map((r) => formatDate(r.as_of))));
  const keepLabel = `Keep ${rows.length === 1 ? 'the item' : 'the items'} as of ${dates.length === 1 ? dates[0] : 'their dates'}`;
  return (
    <FlagShell tone="gone">
      <div className="flex items-center gap-2.5">
        <Globe className="h-4 w-4 flex-none text-ink-500" aria-hidden="true" />
        <span className="min-w-0 flex-1 font-semibold text-ink-900">
          {first.source.title ?? first.source.url ?? 'A web page'}
        </span>
        <Chip tone="critical">Gone</Chip>
      </div>
      <div className="mt-1.5 text-meta text-ink-500">
        {first.source.url ? (
          <span className="font-mono text-[11px]">{hostPath(first.source.url)} · </span>
        ) : null}
        on {formatDate(first.flag?.flagged_at)} the site answered that the page doesn’t exist.{' '}
        {rows.length === 1 ? 'This item relies on it:' : `These ${rows.length} items rely on it:`}
      </div>
      <ul className="m-0 mt-2.5 list-none border-t border-ink-100 p-0">
        {rows.map((r) => (
          <li
            key={r.id}
            className="flex items-baseline gap-2.5 border-b border-ink-100 py-[7px] text-body-sm text-ink-900"
          >
            {r.number ? <MonoNo>{r.number}</MonoNo> : null}
            <span className="min-w-0 flex-1">{r.statement}</span>
            <span className="whitespace-nowrap text-meta text-ink-500">
              as of {formatDate(r.as_of)}
            </span>
          </li>
        ))}
      </ul>
      {mode ? (
        <RetireOrKeep
          ids={rows.map((r) => r.id)}
          keepLabel={keepLabel}
          mode={mode}
          setMode={setMode}
        />
      ) : (
        <Actions>
          <WorkspaceButton variant="primary" onClick={() => openForm('find')}>
            Find the new page
          </WorkspaceButton>
          <WorkspaceButton onClick={() => setMode('retire')}>
            Retire {rows.length === 1 ? 'its item' : 'its items'}
          </WorkspaceButton>
          <WorkspaceButton variant="tertiary" onClick={() => setMode('keep')}>
            {keepLabel}
          </WorkspaceButton>
        </Actions>
      )}
    </FlagShell>
  );
}

function ClientNameCard({ row }: { row: KbFlaggedRow }) {
  const { toast } = useToast();
  const detail = useKbItem(row.id);
  const supersede = useItemAction();
  const confirm = useConfirmItems();
  const [mode, setMode] = React.useState<'retire' | 'keep' | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const item = detail.data?.item;

  const replace = async () => {
    if (!item?.source_version_id || !item.segment_id) return;
    setError(null);
    try {
      const made = (await supersede.mutateAsync({
        id: row.id,
        verb: 'supersede',
        body: {
          source_version_id: item.source_version_id,
          segment_id: item.segment_id,
          quote: row.excerpt_text,
          statement: row.statement ?? '',
        },
      })) as { id?: string };
      if (!made.id) return;
      const reply = await confirm.mutateAsync({ ids: [made.id], acknowledge: [made.id] });
      const done = reply.confirmed[0];
      toast({
        message: done
          ? `${done.number} replaces ${row.number} without the name. Both are kept.`
          : 'The copy without the name waits under To review.',
      });
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  return (
    <FlagShell tone="client">
      <FlagHead row={row} />
      <Quote text={row.excerpt_text} className="mt-2.5" />
      <div className="mt-1.5 text-meta text-ink-500">
        A name added to protected names on {formatDate(row.flag?.flagged_at)} is in this item.
        Clients and consultants never see it; replacing the item removes it for good.
      </div>
      {error ? <FormError>{error}</FormError> : null}
      {mode ? (
        <RetireOrKeep ids={[row.id]} keepLabel="" mode={mode} setMode={setMode} />
      ) : (
        <Actions>
          <WorkspaceButton
            variant="primary"
            disabled={!item || supersede.isPending || confirm.isPending}
            onClick={() => void replace()}
          >
            Replace without the name
          </WorkspaceButton>
          <WorkspaceButton variant="tertiary" onClick={() => setMode('retire')}>
            Retire
          </WorkspaceButton>
        </Actions>
      )}
    </FlagShell>
  );
}
